import {
  AccountInfoResponse,
  CONVERSION_COHORTS,
  CONVERSION_STATES,
} from 'types/billing';

export const DEFAULT_MIGRATION_WINDOW_DAYS = 30;
export const DEFAULT_TRIAL_DAYS = 14;

/**
 * Returns true when the account belongs to the migrated-free cohort.
 * Checks conversion identity only — does NOT verify `trial.active`.
 * Callers that need to gate on an active trial must add that check separately.
 *
 * @param accountInfo - The account info response, if present.
 * @returns `true` if conversionState is `trial_active` and conversionCohort is `free`.
 */
export const isMigratedFreeUser = (
  accountInfo: AccountInfoResponse | undefined,
): boolean =>
  accountInfo?.conversionState === CONVERSION_STATES.trialActive &&
  accountInfo?.conversionCohort === CONVERSION_COHORTS.free;

const isPaidMoveCohort = (
  accountInfo: AccountInfoResponse | undefined,
): boolean =>
  accountInfo?.conversionCohort === CONVERSION_COHORTS.annual ||
  accountInfo?.conversionCohort === CONVERSION_COHORTS.monthly;

/**
 * Returns true when the account is a paid-seat customer who should see the
 * "Move to usage-based" UI (popup, banner, sidebar mascot).
 *
 * Conditions:
 * - `canManageBilling` — caller is the billing admin/payer; non-admins see nothing.
 * - `selfServe === false` — account has not yet moved; already-migrated accounts see nothing.
 * - cohort is `annual` or `monthly`, and conversion is either `awaiting_move` (not
 *   yet tried) or `payment_action_required` (tried, parked awaiting reversal consent
 *   — kept "in" the UI so a 3DS-failed user is never stranded).
 *
 * @param accountInfo - The account info response, if present.
 */
export const isMigratedPaidUser = (
  accountInfo: AccountInfoResponse | undefined,
): boolean =>
  accountInfo?.canManageBilling === true &&
  accountInfo?.selfServe === false &&
  (accountInfo?.conversionState === CONVERSION_STATES.awaitingMove ||
    accountInfo?.conversionState === CONVERSION_STATES.paymentActionRequired) &&
  isPaidMoveCohort(accountInfo);

/**
 * Returns true when the account's paid Move is parked awaiting the user's reversal
 * consent (off-session charge needed 3DS). Callers render the consent step directly
 * for this state — in-session or on a later login — so the user can always resume.
 *
 * @param accountInfo - The account info response, if present.
 */
export const isMovePendingReversal = (
  accountInfo: AccountInfoResponse | undefined,
): boolean =>
  accountInfo?.canManageBilling === true &&
  accountInfo?.selfServe === false &&
  accountInfo?.conversionState === CONVERSION_STATES.paymentActionRequired &&
  isPaidMoveCohort(accountInfo);

/**
 * Returns true when the account has reversed the Move (now free self-serve) but
 * hasn't bought a self-serve plan yet. Such an account no longer shows the
 * migration UI (it's self-serve), yet still needs to be nudged into checkout —
 * callers pair this with a "no active subscription" check to stop once they buy.
 * The migration coupon still applies at that checkout (it's date-driven).
 *
 * @param accountInfo - The account info response, if present.
 */
export const isReversedPendingCheckout = (
  accountInfo: AccountInfoResponse | undefined,
): boolean =>
  accountInfo?.canManageBilling === true &&
  accountInfo?.selfServe === true &&
  accountInfo?.conversionState === CONVERSION_STATES.reversed &&
  isPaidMoveCohort(accountInfo);

/**
 * Returns true when a reversed (post-cancel, pre-checkout) account is still within
 * the live migration-coupon window — so it should keep seeing the decaying
 * discount + days-left UI, mirroring the pre-cancel migration offer. Flips false
 * once the coupon expires (`migrationPerk.active !== true`), which hides that UI.
 *
 * @param accountInfo - The account info response, if present.
 */
export const showsReversedMigrationOffer = (
  accountInfo: AccountInfoResponse | undefined,
): boolean =>
  isReversedPendingCheckout(accountInfo) &&
  accountInfo?.migrationPerk?.active === true;

/**
 * Converts a migration deadline ISO string to whole days remaining (≥ 0), or
 * `undefined` when the date is absent or unparseable.
 *
 * Callers must distinguish three states:
 * - `undefined` — date missing or invalid; do NOT treat as urgent.
 * - `0`         — campaign window has closed (truly expired).
 * - `n > 0`     — active migration with n days left.
 *
 * @param migrationEndDate - ISO date string from `AccountInfoResponse.migrationEndDate`.
 */
export const getMigrationDaysRemaining = (
  migrationEndDate: string | undefined,
): number | undefined => {
  if (!migrationEndDate) return undefined;

  const ms = new Date(migrationEndDate).getTime() - Date.now();

  if (!Number.isFinite(ms)) return undefined;

  return Math.max(0, Math.ceil(ms / 86400000));
};

/**
 * Total length of the migration decision window in days, derived from the
 * campaign start and end dates returned by the API.
 * Falls back to 30 when either date is absent or unparseable.
 *
 * @param migrationStartDate - ISO date string from `AccountInfoResponse.migrationStartDate`.
 * @param migrationEndDate   - ISO date string from `AccountInfoResponse.migrationEndDate`.
 */
export const getMigrationWindowDays = (
  migrationStartDate: string | undefined,
  migrationEndDate: string | undefined,
): number => {
  if (!migrationStartDate || !migrationEndDate)
    return DEFAULT_MIGRATION_WINDOW_DAYS;

  const ms =
    new Date(migrationEndDate).getTime() -
    new Date(migrationStartDate).getTime();

  if (!Number.isFinite(ms) || ms <= 0) return DEFAULT_MIGRATION_WINDOW_DAYS;

  return Math.round(ms / 86400000);
};
